import { CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider'
import config from './environments'

/**
 * AWS SDK Cognito Client Configuration
 * Direct integration with AWS Cognito - no Amplify needed!
 */
export class CognitoConfig {
  private static instance: CognitoConfig
  private client: CognitoIdentityProviderClient | null = null
  private isConfigured = false

  private constructor() {
    this.initialize()
  }

  public static getInstance(): CognitoConfig {
    if (!CognitoConfig.instance) {
      CognitoConfig.instance = new CognitoConfig()
    }
    return CognitoConfig.instance
  }

  private initialize(): void {
    try {
      if (!config.features.cognito) {
        console.log('🔧 Cognito integration disabled - waiting for User Pool setup')
        return
      }

      if (!config.cognito.userPoolId || !config.cognito.userPoolWebClientId) {
        console.warn('⚠️ Cognito User Pool details missing - skipping client configuration')
        return
      }

      this.client = new CognitoIdentityProviderClient({
        region: config.aws.region,
      })
      
      this.isConfigured = true
      console.log('✅ AWS SDK Cognito client configured for environment:', config.env)
    } catch (error) {
      console.error('❌ Failed to configure Cognito client:', error)
      this.isConfigured = false
    }
  }

  public getClient(): CognitoIdentityProviderClient | null {
    return this.client
  }

  public isReady(): boolean {
    return this.isConfigured && this.client !== null
  }

  public getUserPoolId(): string {
    return config.cognito.userPoolId
  }

  public getClientId(): string {
    return config.cognito.userPoolWebClientId
  }

  public getRegion(): string {
    return config.aws.region
  }

  public getOAuthDomain(): string | null {
    return config.cognito.oauth?.domain || null
  }

  public buildHostedUIUrl(redirectUri: string): string | null {
    const domain = this.getOAuthDomain()
    if (!domain) return null

    // Use OAuth2 authorize endpoint for classic flow
    const authUrl = new URL(`${domain}/oauth2/authorize`)
    authUrl.searchParams.set('client_id', this.getClientId())
    authUrl.searchParams.set('response_type', 'code')
    authUrl.searchParams.set('scope', 'aws.cognito.signin.user.admin email openid')
    authUrl.searchParams.set('redirect_uri', redirectUri)
    
    console.log('🔗 Generated Hosted UI URL:', authUrl.toString())
    return authUrl.toString()
  }

  public getTokenEndpoint(): string | null {
    const domain = this.getOAuthDomain()
    if (!domain) return null
    return `${domain}/oauth2/token`
  }
}

/**
 * Helper to check if Cognito is properly configured
 */
export const isCognitoConfigured = (): boolean => {
  return config.features.cognito && 
         !!config.cognito.userPoolId && 
         !!config.cognito.userPoolWebClientId
}

/**
 * Development helper - returns what Cognito configuration is needed
 */
export const getCognitoSetupRequirements = () => {
  const missing = []
  
  if (!config.cognito.userPoolId) missing.push('User Pool ID')
  if (!config.cognito.userPoolWebClientId) missing.push('User Pool Web Client ID')
  if (!config.cognito.oauth?.domain) missing.push('Cognito Hosted UI Domain')
  
  return {
    isReady: missing.length === 0,
    missing,
    instructions: missing.length > 0 ? [
      '📋 Setup Instructions for Boss:',
      '1. Create Cognito User Pool in us-east-2 region',
      '2. Create App Client with the following settings:',
      '   - Generate client secret: No (public client)',
      '   - Authentication flows: ALLOW_USER_SRP_AUTH, ALLOW_REFRESH_TOKEN_AUTH',
      '   - OAuth flows: Authorization code grant',
      '   - OAuth scopes: email, openid, profile',
      `   - Callback URLs: ${config.cognito.oauth?.redirectSignIn || 'https://localhost:3000'}`,
      `   - Sign out URLs: ${config.cognito.oauth?.redirectSignOut || 'https://localhost:3000'}`,
      '3. Set up Cognito Hosted UI Domain:',
      '   - Go to App Integration > Domain name',
      '   - Create domain like: your-company.auth.us-east-2.amazoncognito.com',
      '4. Update environment config with:',
      ...missing.map(item => `   - ${item}`)
    ].join('\n') : '✅ All Cognito configuration is ready!'
  }
}

// Export singleton instance and helper functions
export const cognitoConfig = CognitoConfig.getInstance()
export default { CognitoConfig, isCognitoConfigured, getCognitoSetupRequirements }